import { supabase } from '@/integrations/supabase/client';
import { PortfolioService } from './portfolio';
import { CaseStudyService } from './caseStudies';

export interface UnifiedPortfolioItem {
  id: string;
  title: string;
  description: string;
  image_url?: string;
  images?: any[];
  created_at: string;
  updated_at?: string;
  is_featured: boolean;
  status: 'draft' | 'published';
  order?: number;
  profile_id: string;
  item_type: 'shot' | 'case_study';
  slug?: string;
  seo_title?: string;
  seo_description?: string;
  og_image?: string;
  legacy_image_url?: string;
  view_count?: number;  // Add this for view tracking
}

export interface PortfolioOrderUpdate {
  id: string;
  type: 'shot' | 'case_study';
  order: number;
}

export class UnifiedPortfolioService {
  /**
   * Get unified portfolio items (shots + case studies) for a profile
   * @param profileId - The profile ID to fetch items for
   * @param includeDrafts - Whether to include draft items (default: false for public views)
   * @returns Promise<UnifiedPortfolioItem[]> - Array of unified portfolio items
   */
  static async getUnifiedPortfolio(
    profileId: string, 
    includeDrafts: boolean = false
  ): Promise<UnifiedPortfolioItem[]> {
    const { data, error } = await supabase
      .rpc('get_unified_portfolio_items', { 
        p_profile_id: profileId,
        p_include_drafts: includeDrafts 
      });

    if (error) {
      console.error('Error fetching unified portfolio:', error);
      throw new Error(`Failed to fetch unified portfolio: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get only shots from unified portfolio
   * @param profileId - The profile ID to fetch items for
   * @param includeDrafts - Whether to include draft items
   * @returns Promise<UnifiedPortfolioItem[]> - Array of shot items only
   */
  static async getShots(
    profileId: string, 
    includeDrafts: boolean = false
  ): Promise<UnifiedPortfolioItem[]> {
    const items = await this.getUnifiedPortfolio(profileId, includeDrafts);
    return items.filter(item => item.item_type === 'shot');
  }

  /**
   * Get only case studies from unified portfolio
   * @param profileId - The profile ID to fetch items for
   * @param includeDrafts - Whether to include draft items
   * @returns Promise<UnifiedPortfolioItem[]> - Array of case study items only
   */
  static async getCaseStudies(
    profileId: string,
    includeDrafts: boolean = false
  ): Promise<UnifiedPortfolioItem[]> {
    const items = await this.getUnifiedPortfolio(profileId, includeDrafts);
    return items.filter(item => item.item_type === 'case_study');
  }

  /**
   * Update the order of a single portfolio item
   * @param itemId - The ID of the item to update
   * @param itemType - The type of the item ('shot' or 'case_study')
   * @param newOrder - The new order value
   */
  static async updatePortfolioItemOrder(
    itemId: string,
    itemType: 'shot' | 'case_study',
    newOrder: number
  ): Promise<void> {
    if (itemType === 'case_study') {
      // Update case study order_index
      await CaseStudyService.updateCaseStudy({
        id: itemId,
        order_index: newOrder
      });
    } else {
      // Update shot order in JSONB array
      await PortfolioService.updatePortfolioItem(itemId, {
        order: newOrder
      });
    }
  }

  /**
   * Batch update orders for multiple portfolio items
   * Used when reordering items via drag-and-drop
   * @param updates - Array of items with their new order values
   */
  static async batchUpdatePortfolioOrder(
    updates: PortfolioOrderUpdate[]
  ): Promise<void> {
    try {
      // Process all updates in parallel
      await Promise.all(
        updates.map(update =>
          this.updatePortfolioItemOrder(update.id, update.type, update.order)
        )
      );
    } catch (error) {
      console.error('Error batch updating portfolio order:', error);
      throw new Error('Failed to update portfolio order');
    }
  }
}
